import React, { useEffect, useRef, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $patchStyleText } from "@lexical/selection";
import { $createLineBreakNode, $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isElementNode, $isLineBreakNode, $isRangeSelection, $isTextNode, $setSelection, FORMAT_TEXT_COMMAND, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { Bold, Italic, Redo2, Underline, Undo2 } from "lucide-react";

export function readContentRuns(parent = $getRoot(), separateBlocks = true) {
  const runs = [];
  const visit = (node) => {
    if ($isTextNode(node)) {
      const style = node.getStyle();
      const color = /(?:^|;)\s*color:\s*(#[0-9a-f]{6})/i.exec(style)?.[1] || null;
      const fontSize = /(?:^|;)\s*font-size:\s*(\d+)px/i.exec(style)?.[1];
      runs.push({ text: node.getTextContent(), bold: node.hasFormat("bold"), italic: node.hasFormat("italic"), underline: node.hasFormat("underline"), color, font_size: fontSize ? Number(fontSize) : null });
    } else if ($isLineBreakNode(node)) runs.push({ text: "\n" });
    else if ($isElementNode(node)) node.getChildren().forEach(visit);
  };
  parent.getChildren().forEach((node, index) => {
    if (index && separateBlocks) runs.push({ text: "\n" });
    visit(node);
  });
  return runs;
}

function writeContent(block) {
  const root = $getRoot();
  root.clear();
  const paragraph = $createParagraphNode();
  for (const run of block.runs || [{ text: block.text || "", bold: block.bold ?? block.type === "heading", italic: block.italic }]) {
    run.text.split("\n").forEach((text, index) => {
      if (index) paragraph.append($createLineBreakNode());
      if (!text) return;
      const node = $createTextNode(text);
      for (const format of ["bold", "italic", "underline"]) if (run[format]) node.toggleFormat(format);
      node.setStyle(`${run.color ? `color: ${run.color};` : ""}${run.font_size ? `font-size: ${run.font_size}px;` : ""}`);
      paragraph.append(node);
    });
  }
  root.append(paragraph);
}

function EditorControls({ block, label, onChange }) {
  const [editor] = useLexicalComposerContext();
  const callback = useRef(onChange);
  callback.current = onChange;
  const [formats, setFormats] = useState({});
  const selectionRef = useRef(null);

  useEffect(() => editor.registerUpdateListener(({ editorState }) => editorState.read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selectionRef.current = selection.clone();
      setFormats({ bold: selection.hasFormat("bold"), italic: selection.hasFormat("italic"), underline: selection.hasFormat("underline") });
    }
  })), [editor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const text = readContentRuns().map((run) => run.text).join("");
      if (text !== block.text) editor.update(() => writeContent(block), { tag: "external-content" });
    });
  }, [editor, block]);

  const applyStyle = (style) => editor.update(() => {
    let selection = $getSelection();
    if (!$isRangeSelection(selection) && selectionRef.current) {
      selection = selectionRef.current.clone();
      $setSelection(selection);
    }
    if ($isRangeSelection(selection)) $patchStyleText(selection, style);
  });
  const buttonClass = "inline-flex h-8 w-8 items-center justify-center rounded border border-slate-300";
  return <>
    <div role="group" aria-label={`${label} selected text formatting`} className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-2">
      <span className="text-xs font-semibold text-slate-600">Selected text</span>
      {[ ["bold", Bold], ["italic", Italic], ["underline", Underline] ].map(([format, Icon]) => <button type="button" key={format} title={format} aria-label={`${label} selection ${format}`} aria-pressed={Boolean(formats[format])} onMouseDown={(event) => event.preventDefault()} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)} className={`${buttonClass} ${formats[format] ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}><Icon size={15} /></button>)}
      <select aria-label={`${label} selection font size`} defaultValue="" className="rounded border border-slate-300 bg-white px-2 py-1 text-sm" onChange={(event) => applyStyle({ "font-size": event.target.value ? `${event.target.value}px` : null })}><option value="">Default size</option>{[12, 14, 16, 18, 20, 24, 28, 32, 36].map((size) => <option value={size} key={size}>{size} px</option>)}</select>
      <input type="color" aria-label={`${label} selection color`} title="Selected text color" defaultValue="#111111" className="h-8 w-8" onChange={(event) => applyStyle({ color: event.target.value })} />
      <button type="button" title="Undo" aria-label={`${label} undo`} className={buttonClass} onMouseDown={(event) => event.preventDefault()} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}><Undo2 size={15} /></button>
      <button type="button" title="Redo" aria-label={`${label} redo`} className={buttonClass} onMouseDown={(event) => event.preventDefault()} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}><Redo2 size={15} /></button>
    </div>
    <RichTextPlugin contentEditable={<ContentEditable aria-label={`${label} text`} className="min-h-28 whitespace-pre-wrap break-words px-3 py-2 text-sm outline-none" style={{ fontSize: block.font_size || (block.type === "heading" ? 18 : 14), fontWeight: (block.bold ?? block.type === "heading") ? 700 : 400, fontStyle: block.italic ? "italic" : "normal", color: block.color || "#111111", textAlign: block.alignment || "left", textAlignLast: "auto" }} />} ErrorBoundary={LexicalErrorBoundary} />
    <HistoryPlugin />
    <OnChangePlugin ignoreSelectionChange onChange={(state, instance, tags) => {
      if (tags.has("external-content")) return;
      state.read(() => {
        const runs = readContentRuns();
        callback.current({ text: runs.map((run) => run.text).join(""), runs });
      });
    }} />
  </>;
}

export default function FormContentEditor({ block, label, onChange }) {
  const initialBlock = useRef(block);
  return <div className="min-w-0 w-full overflow-hidden rounded-lg border border-slate-300 bg-white">
    <LexicalComposer initialConfig={{ namespace: label, onError: (error) => { throw error; }, editorState: () => writeContent(initialBlock.current), theme: { text: { bold: "font-bold", italic: "italic", underline: "underline" } } }}>
      <EditorControls block={block} label={label} onChange={onChange} />
    </LexicalComposer>
  </div>;
}